//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import {
  arePurchaseOptionsCompatible,
  getCompatibilityErrorMessage,
} from '@campaigns/components/shared/prepaid-purchase-options/PrepaidPurchaseOptionsCompatibilityUtils';

interface SetValueOptions {
  shouldDirty?: boolean;
  shouldTouch?: boolean;
  shouldValidate?: boolean;
}

interface PurchaseOptionFormActions {
  clearPurchaseOptionError: () => void;
  setPurchaseOptionError: (message: string | undefined) => void;
  setPurchaseOptionValue: (
    value: PurchaseOption[],
    options?: SetValueOptions,
  ) => void;
}

interface UsePurchaseOptionSelectionChangeParams {
  formActions: PurchaseOptionFormActions;
  /**
   * Optional function to filter the merged selection before the compatibility
   * check. Use this when some selected options should be excluded from
   * compatibility validation (e.g. expired options in ChangePurchaseOptionModal).
   */
  filterForCompatibilityCheck?: (options: PurchaseOption[]) => PurchaseOption[];
  purchaseOptions: PurchaseOption[];
  selectedOptions: PurchaseOption[];
}

/**
 * Returns a `handleSelectionChange` callback that:
 * 1. Preserves off-page selections (options selected on other paginated pages).
 * 2. Merges them with the newly selected on-page options.
 * 3. Writes the merged result to the `purchaseOption` form field.
 * 4. Runs a compatibility check and sets/clears a form-level error accordingly.
 */
export const usePrepaidPurchaseOptionSelectionChange =
  ({
    filterForCompatibilityCheck,
    formActions: {
      clearPurchaseOptionError,
      setPurchaseOptionError,
      setPurchaseOptionValue,
    },
    purchaseOptions,
    selectedOptions,
  }: UsePurchaseOptionSelectionChangeParams) =>
  (optionIds: string[]) => {
    const offPageSelections = selectedOptions.filter(
      (opt) =>
        !purchaseOptions.some((p) => p.opportunityId === opt.opportunityId),
    );
    const onPageSelections = purchaseOptions.filter((opt) =>
      optionIds.includes(opt.opportunityId),
    );
    const matchedOptions = [...offPageSelections, ...onPageSelections];

    setPurchaseOptionValue(matchedOptions, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    const optionsToCheck = filterForCompatibilityCheck
      ? filterForCompatibilityCheck(matchedOptions)
      : matchedOptions;

    if (!arePurchaseOptionsCompatible(optionsToCheck)) {
      setPurchaseOptionError(getCompatibilityErrorMessage(optionsToCheck));
    } else {
      clearPurchaseOptionError();
    }
  };
